import { authApi, userApi } from '../api/auth.js';
import { setTokens, clearTokens, saveUser, loadUser } from '../api/client.js';
import type { User } from '../types/index.js';

interface AuthState {
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  permissions: string[];
}

export const authStore = {
  state: (() => {
    const cached = loadUser<User>();
    return {
      user: cached,
      isLoggedIn: !!cached,
      isLoading: false,
      permissions: [] as string[],
    } as AuthState;
  })(),

  listeners: [] as Array<(state: AuthState) => void>,

  subscribe(listener: (state: AuthState) => void) {
    this.listeners.push(listener);
    listener(this.state);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  },

  notify() {
    this.listeners.forEach((listener) => listener(this.state));
  },

  async login(username: string, password: string, captchaSolution: string = '') {
    this.state.isLoading = true;
    this.notify();

    try {
      const response = await authApi.login({ username, password, captcha_solution: captchaSolution });
      setTokens(response.access_token, response.refresh_token);
      saveUser(response.user);
      this.state.user = response.user;
      this.state.isLoggedIn = true;
      await this.fetchPermissions();
    } catch (error) {
      throw error;
    } finally {
      this.state.isLoading = false;
      this.notify();
    }
  },

  async logout() {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearTokens();
    this.state.user = null;
    this.state.isLoggedIn = false;
    this.state.permissions = [];
    this.notify();
  },

  async fetchUser() {
    try {
      const user = await userApi.me();
      saveUser(user);
      this.state.user = user;
      this.state.isLoggedIn = true;
      await this.fetchPermissions();
      this.notify();
    } catch {
      this.state.user = null;
      this.state.isLoggedIn = false;
      this.state.permissions = [];
      this.notify();
    }
  },

  async fetchPermissions() {
    try {
      const response = await userApi.getMyPermissions();
      this.state.permissions = response.permissions;
    } catch {
      this.state.permissions = [];
    }
  },

  hasPermission(permission: string): boolean {
    if (this.state.user?.role === 'admin') return true;
    return this.state.permissions.includes(permission);
  },

  hasAnyPermission(permissions: string[]): boolean {
    if (this.state.user?.role === 'admin') return true;
    return permissions.some((p) => this.state.permissions.includes(p));
  },

  hasAllPermissions(permissions: string[]): boolean {
    if (this.state.user?.role === 'admin') return true;
    return permissions.every((p) => this.state.permissions.includes(p));
  },
};
